import div from '@mui/material/Paper';
import _ from 'lodash';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { obsStatuses, obsTypes, printValue, riskLevels } from '../obsUtils';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import moment from 'moment/moment';
import clsx from 'clsx';
import Clamp from 'react-multiline-clamp';
import React, { useEffect, useRef } from 'react';
import AgyleButton from '../../../../../components/ui/buttons/agyleButton';
import AddIcon from '@mui/icons-material/Add';
import { hasPermission, Permissions, user } from '../../../../../store/reducers/user';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useWindowSize } from '../../../../../util/formatter';
import { ptContextBorderEnable } from '../../../../../store/reducers/patient';
import AgyleLoadingSpinner from '../../../../../components/other/agyleLoadingSpinner';
import { getConfig } from '../../../../../util/configUtil';
import { ctxSplitScreen } from '../../../../../store/reducers/patientContext';
import { obsStyles } from '../obsStyle';
import { useTheme } from '@mui/styles';

const ObservationTable = ({
    observations,
    type,
    createObs,
    displayValues,
    disableSave,
    setObs,
    setConfirmCancel,
    onlyView,
    loadingObs,
}) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const classes = obsStyles();
    const { t } = useTranslation();

    // Configs
    const isRetroObs = getConfig('observations.retrospective', false);
    const newRiskAssessment = getConfig('observations.newRiskAssessment', false);
    const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');

    // Scroll to end of table
    const scrollRef = useRef(null);

    const [width] = useWindowSize();

    // Selectors
    const userObject = useSelector(user);
    const borderEnabled = useSelector(ptContextBorderEnable);
    const splitScreen = useSelector(ctxSplitScreen);

    const tableRef = useRef(null);

    useEffect(() => {
        if (!onlyView && !splitScreen?.enabled) {
            const el = document.getElementById('add-new-obs-position');
            let newLeft = 165 + observations.length * 146 + (borderEnabled ? 10 : 0);
            if (newLeft > width - 230) {
                newLeft = width - 230;
            }
            el.style.left = `${newLeft}px`;
        }
    }, [observations, type, width, borderEnabled, splitScreen]);

    const canDelete = (obs) => {
        return !!(
            dispatch(hasPermission(Permissions.AGYLE_CAN_STRIKE_THROUGH_ANY_OBSERVATIONS)) ||
            (dispatch(hasPermission(Permissions.AGYLE_CAN_STRIKE_THROUGH_OBSERVATIONS)) && userObject?.userName === obs?.completedBy)
        );
    };

    const getObsValueColorStyle = (obs, item) => {
        let status = _.get(obs, item.statusField, obsStatuses.completed);
        if (item.hasOwnProperty('scoreField') && item.scoreField) {
            let score = _.get(obs, item.scoreField);
            if (score !== undefined && score != null) {
                if (type === obsTypes.news || type === obsTypes.pews) {
                    // TODO: choose proper colour for PEWS
                    if (item.scoreField === 'totalScore') {
                        let obsColor;
                        if (type === obsTypes.news) {
                            if (score >= 9) obsColor = classes.obsColorRed;
                            else if (score < 9 && score >= 7) obsColor = classes.obsColorAmber;
                            else if (score < 7 && score >= 5) obsColor = classes?.obsColorYellow;
                            else obsColor = classes?.obsColorWhite;
                        } else {
                            if (score >= 13) obsColor = classes?.obsColorRed;
                            else if (score < 13 && score >= 9) obsColor = classes?.obsColorPink;
                            else if (score < 9 && score >= 5) obsColor = classes.obsColorAmber;
                            else if (score < 5 && score >= 1) obsColor = classes?.obsColorYellow;
                            else obsColor = classes?.obsColorWhite;
                        }
                        return obsColor;
                        // if (score >= 5 && score <= 6) {
                        //     return classes.obsColorMedium;
                        // }
                        // if (score >= 7) {
                        //     return classes.obsColorHigh;
                        // }
                        //
                        // let any = _.find(
                        //     displayValues,
                        //     (newsValue) =>
                        //         newsValue.hasOwnProperty('scoreField') &&
                        //         newsValue.scoreField !== 'totalScore' &&
                        //         _.get(obs, item.scoreField) >= 3,
                        // );
                        // if (any) {
                        //     return classes.obsColorLow;
                        // }
                    } else if (item.scoreField === 'riskLevel' && newRiskAssessment && type === obsTypes.pews) {
                        const val = _.get(obs, item.field);
                        switch (val) {
                            case riskLevels.low:
                                return classes.obsColorRiskLow;
                            case riskLevels.medium:
                                return classes.obsColorRiskMedium;
                            case riskLevels.high:
                                return classes.obsColorRiskHigh;
                            case riskLevels.emergency:
                                return classes.obsColorRiskEmergency;
                            default:
                                return classes.obsColorRiskContinue;
                        }
                    } else {
                        if (status !== obsStatuses.completed && status !== obsStatuses.patientCondition) {
                            return;
                        }
                        if (score === 1) {
                            return classes.obsColorLow;
                        }
                        if (score === 2) {
                            return classes.obsColorMedium;
                        }
                        if (score >= 3) {
                            return classes.obsColorHigh;
                        }
                    }
                } else if (type === obsTypes.neuro) {
                    if (item.scoreField === 'totalScore') {
                        // TODO document why this block is empty
                    } else {
                        if (status !== obsStatuses.completed && status !== obsStatuses.patientCondition) {
                            return;
                        }

                        if (score >= 4) {
                            return classes.obsColorLow;
                        } else if (score === 3) {
                            return classes.obsColorMedium;
                        } else if (score >= 1) {
                            return classes.obsColorHigh;
                        }
                        // else {
                        //     return classes.obsColorUnknown;
                        // }
                    }
                }
            }
        }
    };

    /**
     * Calculates the maximum width for the observation table content.
     *
     * @returns {number|string} The maximum width for the observation table content.
     *                          Returns an empty string if the element is not found or there are no observations.
     */
    const getMaxWidth = () => {
        const el = document.getElementById('obs-results-content');
        if (!el || observations?.length === 0) return '';

        const widthFormatted = el.offsetWidth - (splitScreen?.enabled ? 160 : 400);
        const obsWidth = 145 * observations.length;
        return obsWidth > widthFormatted ? widthFormatted : Math.max(obsWidth, widthFormatted - obsWidth);
    };

    /**
     * Calculates the height for the button based on the height of the observation results content.
     * If the element with id 'obs-results-content' is not found, it returns a default height.
     * Otherwise, it calculates the height based on the table's height and the element's height.
     *
     * @returns {Object} An object containing the maxHeight and height properties.
     */
    const getButtonHeight = () => {
        const el = document.getElementById('obs-results-content');
        if (!el) return { maxHeight: 60, height: 60 };

        const tableRefHeight = tableRef.current?.offsetHeight || 0;
        const tableHeight = el.offsetHeight;
        const newHeight = tableRefHeight && tableRefHeight < tableHeight ? tableRefHeight - 10 : tableHeight;

        return { maxHeight: newHeight, height: newHeight };
    };

    return (
        <>
            {!onlyView && splitScreen?.enabled && (
                <div className={classes.addNewButtonUp}>
                    <AgyleButton
                        onClick={createObs}
                        startIcon={<AddIcon />}
                        disabled={!dispatch(hasPermission(Permissions.AGYLE_CAN_ENTER_OBSERVATIONS)) || loadingObs}
                        colour="primary"
                        style={{ width: '100%' }}
                    >
                        {t('Add new')}
                    </AgyleButton>
                </div>
            )}

            <div className={classes.tableStyle} id="obs-table-continer">
                <table className={classes.obsLabelTable}>
                    <tbody>
                        {displayValues.map((item, index) => {
                            if (item.obsTypes && !_.includes(item.obsTypes, type)) return null;
                            if (item.disableOnTable) return null;

                            return (
                                <tr key={index + 'l'} style={{ marginTop: index === 0 && '-10px' }}>
                                    <td
                                        className={clsx([
                                            item.type !== 'button' && classes.obsLabelCell,
                                            item.type === 'button' && classes.obsLabelCellHeader,
                                            // getDisplayColorStyle(item),
                                        ])}
                                    >
                                        <Clamp withTooltip lines={1} maxLines={1}>
                                            <span>{item.description}</span>
                                        </Clamp>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                <Grid item id="content-obs-table" style={{ maxWidth: getMaxWidth() }} ref={tableRef}>
                    <div className="scroll-normal-white" style={{ marginBottom: -10 }}>
                        <table className={classes.obsValueTable} ref={scrollRef}>
                            <tbody>
                                {observations.length > 0 &&
                                    displayValues.map((item, index) => {
                                        if (item.obsTypes && !_.includes(item.obsTypes, type)) return null;
                                        if (item.disableOnTable) return null;

                                        if (item.type === 'button') {
                                            return (
                                                <tr key={'b_' + index} style={{ height: 60, marginTop: index === 0 && '-10px' }}>
                                                    {observations.map((obs, obsIndex) => {
                                                        return (
                                                            <td
                                                                key={'b_' + index + '_' + obsIndex}
                                                                style={{ paddingRight: obsIndex + 1 < observations.length ? 5 : 0 }}
                                                            >
                                                                <Grid container alignItems="center" className={classes.obsValueCellActions}>
                                                                    <Grid item xs={8} style={{ display: 'flex', paddingLeft: 10 }}>
                                                                        <Typography variant="body1">Obs {obsIndex + 1}</Typography>
                                                                    </Grid>

                                                                    <Grid item xs={4}>
                                                                        {obs.status !== obsStatuses.cancelled && !onlyView && (
                                                                            <Tooltip title={t('Cancel Obs')}>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    disabled={!canDelete(obs) || disableSave}
                                                                                    onClick={() => {
                                                                                        setObs(obs);
                                                                                        setConfirmCancel(true);
                                                                                    }}
                                                                                >
                                                                                    <DeleteIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        )}

                                                                        {obs.status === obsStatuses.cancelled && (
                                                                            <Tooltip
                                                                                title={
                                                                                    t('Canceled by') +
                                                                                    ' ' +
                                                                                    obs.canceledByFullName +
                                                                                    ' on ' +
                                                                                    obs.canceledDate?.format(defaultDateTimeFormat)
                                                                                }
                                                                            >
                                                                                <IconButton size="small">
                                                                                    <InfoIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        )}
                                                                    </Grid>

                                                                    <Grid item xs={12} className={classes.completedDate}>
                                                                        {moment(obs[isRetroObs ? 'startedDate' : 'completedDate']).format(
                                                                            defaultDateTimeFormat,
                                                                        )}
                                                                    </Grid>
                                                                </Grid>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        } else {
                                            return (
                                                <tr key={'v_' + index}>
                                                    {observations.map((obs, obsIndex) => {
                                                        const isFirstOrLast = obsIndex === 0 || obsIndex === observations.length - 1;

                                                        return (
                                                            <td
                                                                key={'v_' + index + '_' + obsIndex}
                                                                className={clsx([
                                                                    classes.obsValueCell,
                                                                    obs.status === obsStatuses.cancelled && classes.obsValueCanceledCell,
                                                                    !isFirstOrLast && 'noBorderRadius',
                                                                    obsIndex === 0 && 'noBorderRadiusRight',
                                                                    obsIndex === observations.length - 1 && 'noBorderRadiusLeft',
                                                                ])}
                                                                style={{ paddingRight: obsIndex + 1 < observations.length ? 5 : 0 }}
                                                            >
                                                                <div
                                                                    className={getObsValueColorStyle(obs, item)}
                                                                    style={{
                                                                        display: 'flex',
                                                                        justifyContent: 'center',
                                                                        alignItems: 'center',
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        textAlign: 'center',
                                                                        borderRadius: 8,
                                                                    }}
                                                                >
                                                                    <Clamp withTooltip lines={1} maxLines={1}>
                                                                        {printValue(obs, item)}
                                                                    </Clamp>
                                                                </div>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        }
                                    })}
                            </tbody>
                        </table>
                    </div>
                </Grid>

                {!onlyView && !splitScreen?.enabled && (
                    <div
                        id="add-new-obs-position"
                        className={classes.addNewObservation}
                        style={{
                            maxWidth: getMaxWidth(),
                            ...getButtonHeight(),
                        }}
                    >
                        {loadingObs && <AgyleLoadingSpinner />}

                        {!loadingObs && (
                            <AgyleButton
                                onClick={createObs}
                                startIcon={<AddIcon />}
                                disabled={!dispatch(hasPermission(Permissions.AGYLE_CAN_ENTER_OBSERVATIONS)) || loadingObs}
                                colour="primary"
                                className={classes.addNewButton}
                                style={{ width: '100%', pointerEvents: 'all' }}
                            >
                                {t('Add new')}
                            </AgyleButton>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default ObservationTable;
